import {
  Box,
  Flex,
  HStack,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  VStack,
} from '@chakra-ui/react';

/**
 * Skeleton pour la liste des conversations pendant le chargement.
 * Reproduit la structure visuelle de ConversationLine.
 */
export const ConversationListSkeleton: React.FC<{itemCount?: number}> = ({
  itemCount = 3,
}) => {
  return (
    <Box
      borderLeft='1px solid'
      borderRight='1px solid'
      borderBottom='1px solid'
      borderColor='gray.light'
      overflow='hidden'
    >
      {[...Array(itemCount)].map((_, index) => (
        <Box
          key={index}
          p={4}
          borderTop='1px solid'
          borderColor='gray.light'
        >
          <HStack
            spacing={4}
            align='center'
          >
            <Flex align='center'>
              <SkeletonCircle size='8' />
              <Box ml='-0.3rem'>
                <SkeletonCircle size='8' />
              </Box>
            </Flex>
            <VStack
              align='flex-start'
              spacing={1}
              flex={1}
              minW={0}
            >
              <HStack
                spacing={2}
                w='100%'
                justify='space-between'
              >
                <Skeleton
                  height='14px'
                  width='60%'
                />
                <Skeleton
                  height='12px'
                  width='50px'
                />
              </HStack>
              <Box
                w='100%'
                bg='blue.bg_light'
                borderRadius='8px'
                padding='12px'
              >
                <Skeleton
                  height='14px'
                  width='80%'
                />
              </Box>
            </VStack>
          </HStack>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Skeleton pour une ligne de conversation pendant le chargement des données client/contact.
 */
export const ConversationLineSkeleton: React.FC<{isSelected?: boolean}> = ({
  isSelected,
}) => (
  <Box
    p={4}
    border={isSelected ? '1px solid' : 'none'}
    borderTop='1px solid'
    borderColor={isSelected ? 'gray.dark' : 'gray.light'}
    bg={isSelected ? 'gray.bg' : 'white'}
  >
    <HStack
      spacing={4}
      align='center'
    >
      <Flex align='center'>
        <SkeletonCircle size='8' />
        <Box ml='-0.3rem'>
          <SkeletonCircle size='8' />
        </Box>
      </Flex>
      <VStack
        align='flex-start'
        spacing={1}
        flex={1}
        minW={0}
      >
        <HStack
          spacing={2}
          w='100%'
          justify='space-between'
        >
          <Skeleton
            height='14px'
            width='60%'
          />
          <Skeleton
            height='12px'
            width='50px'
          />
        </HStack>
        <Box
          w='100%'
          bg='blue.bg_light'
          borderRadius='8px'
          padding='12px'
        >
          <Skeleton
            height='14px'
            width='80%'
          />
        </Box>
      </VStack>
    </HStack>
  </Box>
);

/**
 * Skeleton pour les avatars des participants (groupe).
 */
export const AvatarsSkeleton: React.FC = () => (
  <>
    <SkeletonCircle size='8' />
    <Box ml='-0.3rem'>
      <SkeletonCircle size='8' />
    </Box>
  </>
);

/**
 * Skeleton pour un avatar individuel de participant.
 */
export const ParticipantAvatarSkeleton: React.FC<{avecNom?: boolean}> = ({
  avecNom = false,
}) => {
  if (!avecNom) {
    return <SkeletonCircle size='6' />;
  }
  return (
    <HStack spacing={2}>
      <SkeletonCircle size='6' />
      <Skeleton
        height='14px'
        width='80px'
      />
    </HStack>
  );
};

/**
 * Skeleton pour un message.
 */
export const MessageSkeleton: React.FC<{alignRight?: boolean}> = ({
  alignRight = false,
}) => (
  <HStack
    justify={alignRight ? 'flex-end' : 'flex-start'}
    w='100%'
  >
    <Box
      bg={alignRight ? 'gray.dark' : 'white'}
      borderRadius='8px'
      p={3}
      maxW='70%'
    >
      <SkeletonText
        noOfLines={2}
        spacing={2}
        skeletonHeight='12px'
        width='200px'
      />
      <Skeleton
        height='10px'
        width='50px'
        mt={2}
      />
    </Box>
  </HStack>
);

/**
 * Skeleton pour la liste des messages.
 */
export const MessageListSkeleton: React.FC = () => (
  <VStack
    spacing={4}
    align='stretch'
    h='100%'
  >
    <MessageSkeleton alignRight={false} />
    <MessageSkeleton alignRight />
    <MessageSkeleton alignRight={false} />
    <MessageSkeleton alignRight />
  </VStack>
);

/**
 * Skeleton pour les badges des participants.
 */
export const ParticipantsBadgeSkeleton: React.FC = () => (
  <HStack
    spacing={4}
    mb={3}
    borderBottom='1px solid'
    borderColor='gray.light'
    pb={4}
  >
    <HStack>
      <SkeletonCircle size='8' />
      <Skeleton
        height='14px'
        width='80px'
      />
    </HStack>
    <HStack>
      <SkeletonCircle size='8' />
      <Skeleton
        height='14px'
        width='80px'
      />
    </HStack>
  </HStack>
);

/**
 * Skeleton pour le détail de la conversation.
 */
export const ConversationDetailSkeleton: React.FC = () => (
  <Box
    borderLeft='1px solid'
    borderColor='gray.light'
    h={{base: '100vh', md: 'calc(100vh - 200px)'}}
    display='flex'
    flexDirection='column'
  >
    {/* En-tête desktop skeleton */}
    <Box
      p={4}
      bg='white'
      display={{base: 'none', md: 'block'}}
    >
      <Skeleton
        height='14px'
        width='150px'
        mb={2}
      />
      <HStack
        spacing={4}
        mb={3}
        borderBottom='1px solid'
        borderColor='gray.light'
        pb={4}
      >
        <HStack>
          <SkeletonCircle size='8' />
          <Skeleton
            height='14px'
            width='100px'
          />
        </HStack>
        <HStack>
          <SkeletonCircle size='8' />
          <Skeleton
            height='14px'
            width='100px'
          />
        </HStack>
      </HStack>
      <Skeleton
        height='14px'
        width='200px'
      />
    </Box>
    {/* Zone des messages skeleton */}
    <Box
      flex={1}
      p={4}
      bg='gray.bg'
    >
      <VStack
        spacing={4}
        align='stretch'
      >
        <MessageSkeleton alignRight={false} />
        <MessageSkeleton alignRight />
        <MessageSkeleton alignRight={false} />
      </VStack>
    </Box>
  </Box>
);
